import React from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import getConfig from 'next/config';

import {classNames} from '~/src/utilities/helpers/css';
import {toPascalCase} from '~/src/utilities/helpers/string';

import css from './TickerIcon.module.scss';

const {publicRuntimeConfig: config} = getConfig();

const icons = {
    Break: dynamic(() => import('~/src/components/svg/ticker/Break')),
    Breakball: dynamic(() => import('~/src/components/svg/ticker/Breakball')),
    CancelCircle: dynamic(() => import('~/src/components/svg/ticker/CancelCircle')),
    CheckCircle: dynamic(() => import('~/src/components/svg/ticker/CheckCircle')),
    GameEnd: dynamic(() => import('~/src/components/svg/ticker/GameEnd')),
    GameStart: dynamic(() => import('~/src/components/svg/ticker/GameStart')),
    GoalIcehockey: dynamic(() => import('~/src/components/svg/ticker/GoalIcehockey')),
    GoalSoccer: dynamic(() => import('~/src/components/svg//ticker/GoalSoccer')),
    HalftimeEnd: dynamic(() => import('~/src/components/svg/ticker/HalftimeEnd')),
    HalftimeStart: dynamic(() => import('~/src/components/svg/ticker/HalftimeStart')),
    Match: dynamic(() => import('~/src/components/svg/ticker/Match')),
    Matchball: dynamic(() => import('~/src/components/svg/ticker/Matchball')),
    OvertimeEnd: dynamic(() => import('~/src/components/svg/ticker/OvertimeEnd')),
    OvertimeStart: dynamic(() => import('~/src/components/svg/ticker/OvertimeStart')),
    Penalty: dynamic(() => import('~/src/components/svg/ticker/Penalty')),
    PeriodEnd: dynamic(() => import('~/src/components/svg/ticker/PeriodEnd')),
    PeriodStart: dynamic(() => import('~/src/components/svg/ticker/PeriodStart')),
    RedCard: dynamic(() => import('~/src/components/svg/ticker/RedCard')),
    Set: dynamic(() => import('~/src/components/svg/ticker/Set')),
    Setball: dynamic(() => import('~/src/components/svg/ticker/Setball')),
    ShootOutEnd: dynamic(() => import('~/src/components/svg/ticker/ShootOutEnd')),
    ShootOutHit: dynamic(() => import('~/src/components/svg/ticker/ShootOutHit')),
    ShootOutMiss: dynamic(() => import('~/src/components/svg/ticker/ShootOutMiss')),
    ShootOutStart: dynamic(() => import('~/src/components/svg/ticker/ShootOutStart')),
    Substitution: dynamic(() => import('~/src/components/svg/ticker/Substitution')),
    Timeout: dynamic(() => import('~/src/components/svg/ticker/Timeout')),
    YellowCard: dynamic(() => import('~/src/components/svg/ticker/YellowCard'))
};
const getIcon = (name) => icons[name];

class TickerIcon extends React.Component {
    hasGameMinute() {
        const data = this.props.data;
        return data && typeof data.gameMinute === 'number' && data.gameMinute > 0;
    }

    isLongNumber() {
        const data = this.props.data;
        return this.hasGameMinute() && data.gameMinute.toString().length >= 3;
    }

    hasTime() {
        const data = this.props.data;
        return data && data.displayTime && data.dateTime;
    }

    getTimeString() {
        const data = this.props.data;
        return this.hasGameMinute()
            ? data.gameMinute + "'"
            : this.hasTime()
            ? new Date(data.dateTime).toLocaleTimeString(config.localeCode, config.dateFormatShort)
            : '';
    }

    showTime() {
        return this.props.showTime && (this.hasGameMinute() || this.hasTime());
    }

    isYellowCard() {
        const {type, data} = this.props;
        return type === 'card' && data && data.card === 'YELLOW_CARD';
    }

    isRedCard() {
        const {type, data} = this.props;
        return type === 'card' && data && data.card === 'RED_CARD';
    }

    getIconName() {
        const {type, data} = this.props;
        let iconName;
        switch (type) {
            case 'timeEvent':
                iconName = data.timeEvent;
                break;
            case 'card':
                iconName = data.card;
                break;
            case 'shootOut':
                iconName = data.penaltyShot.some((team) => team[0] === 1) ? 'shoot_out_hit' : 'shoot_out_miss';
                break;
            case 'penalty':
                iconName = type;
                break;
            case 'CheckCircle':
            case 'CancelCircle':
                return type;
            default:
                if (['Break', 'Breakball', 'Setball', 'Matchball'].includes(data?.tennisBreak)) {
                    iconName = data.tennisBreak;
                }

                if (['set', 'tiebreak', 'match', 'substitution', 'goal'].includes(type)) {
                    let newName = type;
                    if (type === 'goal' && this.props.tickerType) {
                        newName = 'goal_' + this.props.tickerType;
                    }
                    iconName = newName;
                }
        }
        if (iconName) {
            return toPascalCase(iconName);
        }
        return iconName;
    }

    renderIcon(iconName) {
        if (!iconName) {
            return null;
        }
        const Icon = getIcon(iconName);

        if (!Icon) {
            return null;
        }
        return <Icon {...this.props.data} />;
    }

    render() {
        const svgIconName = this.getIconName();
        const iconClassName = svgIconName && svgIconName.charAt(0).toLowerCase() + svgIconName.substr(1);

        const iconClass = classNames(css, 'tickerIcon', {
            gameMinuteShort: this.showTime() && !this.isLongNumber(),
            gameMinuteLong: this.showTime() && this.isLongNumber(),
            dateTime: this.showTime() && this.hasTime(),
            yellowCard: this.showTime() && this.isYellowCard(),
            redCard: this.showTime() && this.isRedCard(),
            goal: this.showTime() && this.props.type === 'goal',
            penalty: !this.showTime() && this.props.type === 'penalty',
            svgIcon: !this.showTime() && svgIconName,
            [css[iconClassName]]: !this.showTime()
        });

        return <div className={iconClass}>{this.showTime() ? <span>{this.getTimeString()}</span> : this.renderIcon(svgIconName)}</div>;
    }
}

if (process.env.NODE_ENV === 'development') {
    TickerIcon.propTypes = {
        type: PropTypes.string.isRequired,
        tickerType: PropTypes.string,
        showTime: PropTypes.bool
    };
}

export default TickerIcon;
